/**
 * Professor Comments System
 * Handles comment submission, likes, dislikes, replies, and reports
 */

// Helper function to show message in comment area
function showMessage(type, message) {
    const errorDiv = document.getElementById('comment-error');
    const successDiv = document.getElementById('comment-success');

    if (type === 'error') {
        if (errorDiv) {
            errorDiv.querySelector('p').textContent = message;
            errorDiv.classList.remove('hidden');
        }
        if (successDiv) {
            successDiv.classList.add('hidden');
        }
    } else {
        if (successDiv) {
            successDiv.querySelector('p').textContent = message;
            successDiv.classList.remove('hidden');
        }
        if (errorDiv) {
            errorDiv.classList.add('hidden');
        }
    }

    // Auto-hide after 5 seconds
    setTimeout(() => {
        if (errorDiv) errorDiv.classList.add('hidden');
        if (successDiv) successDiv.classList.add('hidden');
    }, 5000);
}

// Show login prompt with Farsi message and open auth modal
function showLoginPrompt() {
    const errorDiv = document.getElementById('comment-error');
    const successDiv = document.getElementById('comment-success');

    if (successDiv) successDiv.classList.add('hidden');

    if (errorDiv) {
        errorDiv.innerHTML = `
            <div class="flex items-center justify-between">
                <p class="text-red-800 text-sm">
                    <i class="fas fa-sign-in-alt ml-1"></i>
                    برای ارسال نظر، لطفا ابتدا وارد حساب کاربری خود شوید.
                </p>
                <button type="button" onclick="document.getElementById('auth-modal')?.dispatchEvent(new CustomEvent('open-modal'))"
                    class="mr-3 flex-shrink-0 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">
                    ورود / ثبت‌نام
                </button>
            </div>
        `;
        errorDiv.classList.remove('hidden');
    }

    // Also try opening the auth modal directly
    document.getElementById('auth-modal')?.dispatchEvent(new CustomEvent('open-modal'));
}

// Handle comment form submission
function setupCommentForm() {
    const commentForm = document.getElementById('comment-form');
    if (!commentForm) return;

    // Store the comment data to retry after profile completion
    let pendingCommentData = null;

    const submitComment = (retryData = null) => {
        const textarea = document.getElementById('comment-text');
        const submitBtn = document.getElementById('comment-submit-btn');
        const text = retryData ? retryData.text : textarea.value.trim();
        const professorId = retryData ? retryData.professorId : commentForm.dataset.professorId;

        if (!text) {
            showMessage('error', 'لطفا نظر خود را بنویسید');
            return;
        }

        // Disable button and show loading
        submitBtn.disabled = true;
        const originalText = submitBtn.querySelector('span').textContent;
        submitBtn.querySelector('span').textContent = 'در حال ارسال...';

        fetch('/comment/ajax/store', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
            },
            body: JSON.stringify({
                text: text,
                type: 'App\\Models\\Features\\Professors\\Professor',
                commentable_id: professorId,
            })
        })
            .then(async response => {
                let data;
                try {
                    data = await response.json();
                } catch (e) {
                    // Non-JSON response (e.g. HTML redirect page)
                    if (response.status === 401 || response.status === 419) {
                        pendingCommentData = { text, professorId };
                        showLoginPrompt();
                        throw new Error('UNAUTHENTICATED');
                    }
                    throw new Error('خطا در ارسال نظر. لطفا دوباره تلاش کنید.');
                }

                if (!response.ok) {
                    // Handle unauthenticated (401, 419, or 500 with "Unauthenticated" message)
                    if (response.status === 401 || response.status === 419 ||
                        (data.message && data.message.toLowerCase().includes('unauthenticated'))) {
                        pendingCommentData = { text, professorId };
                        showLoginPrompt();
                        throw new Error('UNAUTHENTICATED');
                    }

                    // Handle 403 - Profile incomplete
                    if (response.status === 403) {
                        // Store comment data for retry
                        pendingCommentData = { text, professorId };

                        showMessage('error', data.message || 'لطفا ابتدا اطلاعات پروفایل خود را تکمیل کنید.');

                        // Open profile completion modal with retry callback
                        window.dispatchEvent(new CustomEvent('open-profile-modal', {
                            detail: {
                                callback: () => {
                                    // Retry comment submission after profile completion
                                    setTimeout(() => {
                                        submitComment(pendingCommentData);
                                        pendingCommentData = null;
                                    }, 500);
                                }
                            }
                        }));

                        throw new Error('PROFILE_INCOMPLETE');
                    }

                    // Handle other errors
                    throw new Error(data.message || 'خطا در ارسال نظر');
                }

                return data;
            })
            .then(data => {
                if (data.status === 200 && data.success) {
                    showMessage('success', 'نظر شما با موفقیت ثبت شد.');

                    // Create and insert the new comment
                    const commentsList = document.getElementById('comments-list');
                    const noCommentsMsg = document.getElementById('no-comments-message');

                    // Hide "no comments" message if exists
                    if (noCommentsMsg) {
                        noCommentsMsg.remove();
                    }

                    // Get user info (from meta or data attributes)
                    const userFirstName = document.querySelector('meta[name="user-first-name"]')?.content || '';
                    const userLastName = document.querySelector('meta[name="user-last-name"]')?.content || '';
                    const userName = `${userFirstName} ${userLastName}`.trim() || 'کاربر';
                    const userAvatar = document.querySelector('meta[name="user-avatar"]')?.content || '';

                    // Create new comment HTML
                    const newCommentHtml = `
                    <div class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow comment-item animate-fade-in">
                        <div class="flex items-start gap-3">
                            <div class="flex-shrink-0">
                                ${userAvatar ?
                            `<img src="${userAvatar}" alt="${userName}" class="w-10 h-10 rounded-full object-cover">` :
                            `<div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                        <i class="fas fa-user text-gray-600"></i>
                                    </div>`
                        }
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center gap-2 mb-2">
                                    <h4 class="font-semibold text-gray-900">${userName}</h4>
                                    <span class="text-sm text-gray-500">الان</span>
                                </div>
                                <p class="text-gray-700 leading-relaxed mb-3">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
                                <div class="flex items-center gap-4">
                                    <button class="comment-like-btn flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors text-sm"
                                            data-comment-id="${data.comment?.id || ''}" disabled>
                                        <i class="fas fa-thumbs-up"></i>
                                        <span class="like-count">0</span>
                                    </button>
                                    <button class="comment-dislike-btn flex items-center gap-1 text-gray-500 hover:text-red-600 transition-colors text-sm"
                                            data-comment-id="${data.comment?.id || ''}" disabled>
                                        <i class="fas fa-thumbs-down"></i>
                                        <span class="dislike-count">0</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                `;

                    // Insert at the beginning of comments list
                    if (commentsList) {
                        commentsList.insertAdjacentHTML('afterbegin', newCommentHtml);
                    }

                    // Clear textarea
                    textarea.value = '';
                }
            })
            .catch(error => {
                // For profile incomplete or unauthenticated, error message is already shown above
                if (error.message === 'PROFILE_INCOMPLETE' || error.message === 'UNAUTHENTICATED') {
                    return;
                }

                // Handle other errors
                const errorMessage = error.message;
                showMessage('error', errorMessage);
            })
            .finally(() => {
                submitBtn.disabled = false;
                submitBtn.querySelector('span').textContent = originalText;
            });
    };

    // Handle form submission
    commentForm.addEventListener('submit', function (e) {
        e.preventDefault();
        e.stopPropagation();
        submitComment();
        return false;
    });

    // Also handle button click as backup (in case form submit doesn't fire)
    const submitBtn = document.getElementById('comment-submit-btn');
    if (submitBtn) {
        submitBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            submitComment();
            return false;
        });
    }
}

// Helper: apply/remove the active visual state on like/dislike buttons.
// activeVote is one of 'like' | 'dislike' | null.
function applyVoteState(commentItem, activeVote) {
    if (!commentItem) return;
    const likeBtn = commentItem.querySelector('.comment-like-btn');
    const dislikeBtn = commentItem.querySelector('.comment-dislike-btn');

    if (likeBtn) {
        likeBtn.classList.toggle('text-blue-600', activeVote === 'like');
        likeBtn.classList.toggle('font-semibold', activeVote === 'like');
        likeBtn.classList.toggle('text-gray-500', activeVote !== 'like');
    }
    if (dislikeBtn) {
        dislikeBtn.classList.toggle('text-red-600', activeVote === 'dislike');
        dislikeBtn.classList.toggle('font-semibold', activeVote === 'dislike');
        dislikeBtn.classList.toggle('text-gray-500', activeVote !== 'dislike');
    }
}

// Handle comment like button
function setupCommentLikes() {
    document.addEventListener('click', function (e) {
        const likeBtn = e.target.closest('.comment-like-btn');
        if (likeBtn) {
            e.preventDefault();
            const commentId = likeBtn.dataset.commentId;

            if (!commentId || likeBtn.disabled) return;

            likeBtn.disabled = true;
            likeBtn.style.opacity = '0.6';

            fetch(`/professors/comments/${commentId}/like`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                }
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        const commentItem = likeBtn.closest('.comment-item');
                        const likeCountEl = likeBtn.querySelector('.like-count');
                        if (likeCountEl) {
                            likeCountEl.textContent = data.likes_count;
                        }
                        const dislikeBtn = likeBtn.closest('.flex').querySelector('.comment-dislike-btn');
                        if (dislikeBtn) {
                            const dislikeCountEl = dislikeBtn.querySelector('.dislike-count');
                            if (dislikeCountEl) {
                                dislikeCountEl.textContent = data.dislikes_count;
                            }
                        }
                        // Update active state based on server's user_vote, with count-based fallback
                        applyVoteState(commentItem, data.user_vote === 'like' ? 'like' : null);
                    } else if (data.error) {
                        showMessage('error', data.message);
                    }
                })
                .catch(error => {
                    console.error('Error:', error);
                    showMessage('error', 'خطا در ثبت لایک');
                })
                .finally(() => {
                    likeBtn.disabled = false;
                    likeBtn.style.opacity = '1';
                });
        }
    });
}

// Handle comment dislike button
function setupCommentDislikes() {
    document.addEventListener('click', function (e) {
        const dislikeBtn = e.target.closest('.comment-dislike-btn');
        if (dislikeBtn) {
            e.preventDefault();
            const commentId = dislikeBtn.dataset.commentId;

            if (!commentId || dislikeBtn.disabled) return;

            dislikeBtn.disabled = true;
            dislikeBtn.style.opacity = '0.6';

            fetch(`/professors/comments/${commentId}/dislike`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                }
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        const commentItem = dislikeBtn.closest('.comment-item');
                        const dislikeCountEl = dislikeBtn.querySelector('.dislike-count');
                        if (dislikeCountEl) {
                            dislikeCountEl.textContent = data.dislikes_count;
                        }
                        const likeBtn = dislikeBtn.closest('.flex').querySelector('.comment-like-btn');
                        if (likeBtn) {
                            const likeCountEl = likeBtn.querySelector('.like-count');
                            if (likeCountEl) {
                                likeCountEl.textContent = data.likes_count;
                            }
                        }
                        // Update active state based on server's user_vote, with count-based fallback
                        applyVoteState(commentItem, data.user_vote === 'dislike' ? 'dislike' : null);
                    } else if (data.error) {
                        showMessage('error', data.message);
                    }
                })
                .catch(error => {
                    console.error('Error:', error);
                    showMessage('error', 'خطا در ثبت دیسلایک');
                })
                .finally(() => {
                    dislikeBtn.disabled = false;
                    dislikeBtn.style.opacity = '1';
                });
        }
    });
}

// Handle comment reply button
function setupCommentReplies() {
    document.addEventListener('click', function (e) {
        const replyBtn = e.target.closest('.comment-reply-btn');
        if (replyBtn) {
            e.preventDefault();
            const commentItem = replyBtn.closest('.comment-item');
            const replyFormContainer = commentItem.querySelector('.reply-form-container');

            if (replyFormContainer) {
                // Toggle reply form visibility
                replyFormContainer.classList.toggle('hidden');

                // Focus on textarea
                if (!replyFormContainer.classList.contains('hidden')) {
                    const textarea = replyFormContainer.querySelector('textarea');
                    if (textarea) textarea.focus();
                }
            }
        }
    });
}

// Handle reply form submission
function setupReplyForm() {
    const commentForm = document.getElementById('comment-form');

    document.addEventListener('submit', function (e) {
        if (e.target.classList.contains('reply-form')) {
            e.preventDefault();

            const form = e.target;
            const textarea = form.querySelector('textarea');
            const submitBtn = form.querySelector('button[type="submit"]');
            const text = textarea.value.trim();
            const parentId = form.dataset.parentId;
            const professorId = commentForm?.dataset.professorId;

            if (!text) {
                showMessage('error', 'لطفا پاسخ خود را بنویسید');
                return;
            }

            // Disable button and show loading
            submitBtn.disabled = true;
            const originalText = submitBtn.textContent;
            submitBtn.textContent = 'در حال ارسال...';

            const submitReply = () => {
                fetch('/comment/ajax/store', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        text: text,
                        type: 'App\\Models\\Features\\Professors\\Professor',
                        commentable_id: professorId,
                        reply_to: parentId,
                    })
                })
                    .then(async response => {
                        let data;
                        try {
                            data = await response.json();
                        } catch (e) {
                            if (response.status === 401 || response.status === 419) {
                                showLoginPrompt();
                                throw new Error('UNAUTHENTICATED');
                            }
                            throw new Error('خطا در ارسال پاسخ. لطفا دوباره تلاش کنید.');
                        }

                        if (!response.ok) {
                            // Handle unauthenticated (401, 419, or 500 with "Unauthenticated" message)
                            if (response.status === 401 || response.status === 419 ||
                                (data.message && data.message.toLowerCase().includes('unauthenticated'))) {
                                showLoginPrompt();
                                throw new Error('UNAUTHENTICATED');
                            }

                            // Handle 403 - Profile incomplete
                            if (response.status === 403) {
                                showMessage('error', data.message || 'لطفا ابتدا اطلاعات پروفایل خود را تکمیل کنید.');

                                // Open profile completion modal with retry callback
                                window.dispatchEvent(new CustomEvent('open-profile-modal', {
                                    detail: {
                                        callback: () => {
                                            // Retry reply submission after profile completion
                                            setTimeout(() => submitReply(), 500);
                                        }
                                    }
                                }));

                                throw new Error('PROFILE_INCOMPLETE');
                            }

                            // Handle other errors
                            throw new Error(data.message || 'خطا در ارسال پاسخ');
                        }

                        return data;
                    })
                    .then(data => {
                        if (data.status === 200 && data.success) {
                            showMessage('success', 'پاسخ شما با موفقیت ثبت شد و در انتظار تأیید است');

                            // Get user info
                            const userFirstName = document.querySelector('meta[name="user-first-name"]')?.content || '';
                            const userLastName = document.querySelector('meta[name="user-last-name"]')?.content || '';
                            const userName = `${userFirstName} ${userLastName}`.trim() || 'کاربر';
                            const userAvatar = document.querySelector('meta[name="user-avatar"]')?.content || '';

                            // Create new reply HTML
                            const newReplyHtml = `
                            <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 animate-fade-in">
                                <div class="flex items-start gap-2">
                                    <div class="flex-shrink-0">
                                        ${userAvatar ?
                                    `<img src="${userAvatar}" alt="${userName}" class="w-8 h-8 rounded-full object-cover">` :
                                    `<div class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                                <i class="fas fa-user text-gray-600 text-xs"></i>
                                            </div>`
                                }
                                    </div>
                                    <div class="flex-1">
                                        <div class="flex items-center gap-2 mb-1">
                                            <h5 class="font-semibold text-gray-900 text-sm">${userName}</h5>
                                            <span class="text-xs text-gray-500">الان</span>
                                            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                <i class="fas fa-clock ml-1 text-xs"></i>
                                                در انتظار تأیید
                                            </span>
                                        </div>
                                        <p class="text-gray-700 text-sm leading-relaxed">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>
                                    </div>
                                </div>
                            </div>
                        `;

                            // Find or create replies container
                            const commentItem = form.closest('.comment-item');
                            let repliesContainer = commentItem.querySelector('.mt-4.mr-8');

                            if (!repliesContainer) {
                                // Create new replies container if doesn't exist
                                repliesContainer = document.createElement('div');
                                repliesContainer.className = 'mt-4 mr-8 space-y-3';
                                const contentDiv = form.closest('.flex-1');
                                contentDiv.appendChild(repliesContainer);
                            }

                            // Insert the new reply
                            repliesContainer.insertAdjacentHTML('beforeend', newReplyHtml);

                            // Clear and hide form
                            textarea.value = '';
                            form.closest('.reply-form-container').classList.add('hidden');
                        }
                    })
                    .catch(error => {
                        // For profile incomplete or unauthenticated, error message is already shown above
                        if (error.message === 'PROFILE_INCOMPLETE' || error.message === 'UNAUTHENTICATED') {
                            return;
                        }

                        // Handle other errors
                        const errorMessage = error.message;
                        showMessage('error', errorMessage);
                    })
                    .finally(() => {
                        submitBtn.disabled = false;
                        submitBtn.textContent = originalText;
                    });
            };

            submitReply();
        }
    });
}

// Handle comment report button
function setupCommentReports() {
    document.addEventListener('click', function (e) {
        const reportBtn = e.target.closest('.comment-report-btn');
        if (reportBtn) {
            e.preventDefault();
            const commentId = reportBtn.dataset.commentId;

            if (!commentId) return;

            // Simple confirmation dialog
            if (confirm('آیا می‌خواهید این نظر را گزارش دهید؟')) {
                reportBtn.disabled = true;
                reportBtn.style.opacity = '0.6';

                fetch(`/professors/comments/${commentId}/report`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({
                        reason_index: 1, // Default reason
                        comments: ''
                    })
                })
                    .then(async response => {
                        let data;
                        try {
                            data = await response.json();
                        } catch (err) {
                            // Non-JSON response (e.g. an HTML redirect from the auth/email.verified middleware)
                            if (response.status === 401 || response.status === 419) {
                                showLoginPrompt();
                                throw new Error('UNAUTHENTICATED');
                            }
                            if (response.status === 403) {
                                showMessage('error', 'برای گزارش، ابتدا ایمیل خود را تأیید کنید.');
                                throw new Error('EMAIL_UNVERIFIED');
                            }
                            throw new Error('خطا در ثبت گزارش');
                        }

                        // Unauthenticated / session expired
                        if (response.status === 401 || response.status === 419) {
                            showLoginPrompt();
                            throw new Error('UNAUTHENTICATED');
                        }

                        // Forbidden: logged-in but email not verified (or otherwise forbidden)
                        if (response.status === 403) {
                            const message = (data && data.message)
                                ? data.message
                                : 'برای گزارش، ابتدا ایمیل خود را تأیید کنید.';
                            showMessage('error', message);
                            throw new Error('FORBIDDEN');
                        }

                        return data;
                    })
                    .then(data => {
                        if (data.success) {
                            showMessage('success', data.message);
                            // Permanently disable the button so the same comment
                            // cannot be reported twice.
                            reportBtn.dataset.reported = '1';
                            reportBtn.style.opacity = '0.3';
                        } else if (data.error) {
                            showMessage('error', data.message);
                        }
                    })
                    .catch(error => {
                        // Already-handled cases — UI feedback was shown above
                        if (error.message === 'UNAUTHENTICATED'
                            || error.message === 'EMAIL_UNVERIFIED'
                            || error.message === 'FORBIDDEN') {
                            return;
                        }
                        console.error('Error:', error);
                        showMessage('error', 'خطا در ثبت گزارش');
                    })
                    .finally(() => {
                        // Re-enable the button unless the report succeeded.
                        if (reportBtn.dataset.reported !== '1') {
                            reportBtn.disabled = false;
                            reportBtn.style.opacity = '1';
                        }
                    });
            }
        }
    });
}

// Track if comments have been initialized to prevent double initialization
let commentsInitialized = false;

// Initialize all comment functionality
export function initComments() {
    // Prevent double initialization
    if (commentsInitialized) {
        return;
    }

    const commentForm = document.getElementById('comment-form');
    if (!commentForm) {
        // Form doesn't exist on this page, skip initialization
        return;
    }

    commentsInitialized = true;
    setupCommentForm();
    setupCommentLikes();
    setupCommentDislikes();
    setupCommentReplies();
    setupReplyForm();
    setupCommentReports();
}

